import React, { useEffect, lazy } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Lock as LockIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Avatar } from '../components/Avatar';
import { CtaButton } from '../components/CtaButton';
const PATTERN = 'https://cdn.magicpatterns.com/uploads/21EmGk8z7MMzrptPTGJweH/Nova_pattern.svg';
const LOGO = 'https://cdn.magicpatterns.com/uploads/x8KNGohUsVwqYiMnUsECdZ/nova_case_study.svg';
const COVER = 'https://cdn.magicpatterns.com/uploads/9aJeu6Hp81NzZUyDYKPAir/Screenshot_2026-07-03_at_12.12.05.png';
const COLLATERAL = 'https://cdn.magicpatterns.com/uploads/wExVB9Wo1JJn6vjYjZzBtQ/nova.png';
const LANDING = 'https://cdn.magicpatterns.com/uploads/ejAiF8wSoB8PaWZ8XBvGqP/Landing_Page.png';
export function NovaCloudCaseStudy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <>
      <div className="glow" data-id="element-501"></div>
      <Navbar data-id="element-502" />

      <main className="wrap" data-id="element-503">
        <section className="sec pb-0" data-id="element-504">
          <Link to="/#work" className="mono text-xs uppercase tracking-wider text-mut hover:text-lime transition-colors inline-block mb-10" data-id="element-505">
            ← All work
          </Link>
          <div className="eyebrow mb-6" data-id="element-506">Case Study</div>
          <h1 className="text-6xl md:text-8xl font-bold tracking-tight mb-8" data-id="element-507">
            NovaCloud Solutions
          </h1>
          <p className="text-xl md:text-2xl text-mut max-w-3xl leading-relaxed" data-id="element-508">
            A cloud services company that needed to look like the future it was
            selling. We built the identity, the collateral, and a website that
            turns "cloud solutions partner" into something you can feel — where
            innovation meets technology.
          </p>
        </section>

        {/* Brand cover — company profile */}
        <section className="sec" data-id="element-509">
          <figure className="border border-line-2 rounded-r-lg overflow-hidden bg-white" data-id="element-510">
            <img src={COVER} alt="NovaCloud company profile cover — where innovation meets technology" className="w-full h-auto block" loading="lazy" data-id="element-511" />
          </figure>
          <figcaption className="mono text-xs uppercase tracking-wider text-mut-2 mt-4" data-id="element-512">
            Company profile — brand cover
          </figcaption>
        </section>

        <section className="sec pt-0" data-id="element-513">
          <div className="stats" data-id="element-514">
            <div className="stat" data-id="element-515">
              <div className="text-xl font-bold text-ink mb-2" data-id="element-516">
                NovaCloud Solutions
              </div>
              <div className="l" data-id="element-517">Client</div>
            </div>
            <div className="stat" data-id="element-518">
              <div className="text-xl font-bold text-ink mb-2" data-id="element-519">
                Cloud · IT Services
              </div>
              <div className="l" data-id="element-520">Industry</div>
            </div>
            <div className="stat" data-id="element-521">
              <div className="text-xl font-bold text-ink mb-2" data-id="element-522">5 weeks</div>
              <div className="l" data-id="element-523">Timeline</div>
            </div>
            <div className="stat" data-id="element-524">
              <div className="text-xl font-bold text-ink mb-2" data-id="element-525">
                Brand · Web Design & Dev
              </div>
              <div className="l" data-id="element-526">Services</div>
            </div>
          </div>
        </section>

        <section className="sec" data-id="element-527">
          <div className="studio" data-id="element-528">
            <div data-id="element-529">
              <h2 className="text-4xl md:text-5xl font-bold mb-8" data-id="element-530">
                The Challenge — cloud is invisible, the brand{' '}
                <span className="serif" data-id="element-531">can't be</span>
              </h2>
            </div>
            <div className="text-lg text-mut leading-relaxed space-y-6" data-id="element-532">
              <p data-id="element-533">
                NovaCloud had the engineering to run serious cloud
                infrastructure, but on paper it looked like every other IT
                vendor — blue gradients, stock server racks, and a name buyers
                forgot as soon as they clicked away. In a category where the
                product is literally invisible, the brand has to carry all the
                confidence.
              </p>
              <p data-id="element-534">
                They needed an identity that felt innovative and
                enterprise-ready at once, plus a site that could turn a cold
                visitor into a "book a call" without a single jargon-filled
                paragraph.
              </p>
            </div>
          </div>
        </section>

        <section className="sec" data-id="element-535">
          <div className="studio" data-id="element-536">
            <div data-id="element-537">
              <h2 className="text-4xl md:text-5xl font-bold mb-8" data-id="element-538">
                The Approach — a mark that{' '}
                <span className="serif" data-id="element-539">radiates</span>
              </h2>
            </div>
            <div className="text-lg text-mut leading-relaxed space-y-6" data-id="element-540">
              <p data-id="element-541">
                The whole system grew out of one idea — a burst radiating
                outward, standing in for reach, energy, and data moving in every
                direction. It became the "o" in the wordmark and a motif we
                could scale across a business card, a cap, or a full-bleed hero.
                We paired a deep Space Cadet navy with a confident Yellow Green:
                navy for the trust an infrastructure partner needs, green for
                momentum. That palette then carried straight into a website
                built to lead with outcomes, not features.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4" data-id="element-542">
                <div className="flex items-center gap-3" data-id="element-543">
                  <span className="w-9 h-9 rounded-full border border-line-2" style={{
                  background: '#222052'
                }} aria-hidden="true" data-id="element-544" />
                  <span className="mono text-xs uppercase tracking-wider text-mut" data-id="element-545">
                    Space Cadet · trust
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-546">
                  <span className="w-9 h-9 rounded-full border border-line-2" style={{
                  background: '#91c640'
                }} aria-hidden="true" data-id="element-547" />
                  <span className="mono text-xs uppercase tracking-wider text-mut" data-id="element-548">
                    Yellow Green · momentum
                  </span>
                </div>
              </div>

              <div className="mt-8 space-y-4" data-id="element-549">
                <div className="p-6 border border-line rounded-r-lg bg-raise" data-id="element-550">
                  <h4 className="font-bold text-ink mb-2" data-id="element-551">
                    One flexible identity
                  </h4>
                  <p className="text-base" data-id="element-552">
                    The sunburst and wordmark hold up from a favicon to signage,
                    merch, and pitch decks without redrawing.
                  </p>
                </div>
                <div className="p-6 border border-line rounded-r-lg bg-raise" data-id="element-553">
                  <h4 className="font-bold text-ink mb-2" data-id="element-554">
                    Outcome-first site
                  </h4>
                  <p className="text-base" data-id="element-555">
                    Designed and built to open with "accelerate your business,"
                    then let the services prove it.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Brand pattern band */}
        <section className="sec pt-0" data-id="element-556">
          <figure className="rounded-r-lg overflow-hidden border border-line" data-id="element-557">
            <div className="h-40 md:h-56 w-full" style={{
            backgroundImage: `url(${PATTERN})`,
            backgroundSize: '220px',
            backgroundRepeat: 'repeat'
          }} role="img" aria-label="NovaCloud brand pattern — repeating sunburst motif" data-id="element-558" />
          </figure>
          <figcaption className="mono text-xs uppercase tracking-wider text-mut-2 mt-4" data-id="element-559">
            Brand pattern — the radiating motif
          </figcaption>
        </section>

        {/* Website landing page — full snippet */}
        <section className="sec" data-id="element-560">
          <div className="sec__head mb-12" data-id="element-561">
            <div data-id="element-562">
              <div className="eyebrow mb-5" data-id="element-563">The Website</div>
              <h2 data-id="element-564">
                A landing page that leads with{' '}
                <span className="serif" data-id="element-565">outcomes</span>.
              </h2>
            </div>
            <p className="text-lg text-mut leading-relaxed max-w-md" data-id="element-566">
              The hero opens on "accelerate your business" with the sunburst
              motif orbiting real infrastructure — proof and personality in a
              single glance.
            </p>
          </div>

          <figure data-id="element-567">
            <div className="border border-line-2 rounded-r-lg overflow-hidden bg-raise-2" data-id="element-568">
              <div className="flex items-center gap-3 px-5 py-3.5 border-b border-line" data-id="element-569">
                <span className="flex gap-2" aria-hidden="true" data-id="element-570">
                  <span className="w-3 h-3 rounded-full bg-[#ff5f57]" data-id="element-571" />
                  <span className="w-3 h-3 rounded-full bg-[#febc2e]" data-id="element-572" />
                  <span className="w-3 h-3 rounded-full bg-[#28c840]" data-id="element-573" />
                </span>
                <span className="ml-3 flex items-center gap-2 flex-1 max-w-md mono text-xs text-mut-2 bg-canvas border border-line rounded-full px-4 py-1.5" data-id="element-574">
                  <LockIcon size={12} data-id="element-575" />
                  www.novacloud-ke.com
                </span>
              </div>
              <img src={LANDING} alt="NovaCloud website landing page — Accelerate your business with Novacloud Solutions" className="w-full h-auto block" loading="lazy" data-id="element-576" />
            </div>
            <figcaption className="mono text-xs uppercase tracking-wider text-mut-2 mt-4" data-id="element-577">
              Marketing site — landing page, above the fold
            </figcaption>
          </figure>
        </section>

        <section className="sec" data-id="element-578">
          <figure className="quote max-w-4xl mx-auto" data-id="element-579">
            <blockquote className="q" data-id="element-580">
              "We finally look like the company we are. Yetu gave us a mark
              people remember and a site that{' '}
              <em data-id="element-581">does the selling before we even pick up the phone</em>."
            </blockquote>
            <figcaption className="who" data-id="element-582">
              <Avatar name="Amos" data-id="element-583" />
              <div data-id="element-584">
                <b data-id="element-585">Amos</b>
                <span data-id="element-586">Co-founder, Nova Cloud</span>
              </div>
            </figcaption>
          </figure>
        </section>

        <section className="sec" data-id="element-587">
          <div className="sec__head mb-12" data-id="element-588">
            <div data-id="element-589">
              <div className="eyebrow mb-5" data-id="element-590">Selected screens</div>
              <h2 data-id="element-591">
                One identity, <span className="serif" data-id="element-592">scaled</span> everywhere.
              </h2>
            </div>
          </div>

          {/* Browser mockup — marketing site */}
          <figure className="mb-6" data-id="element-593">
            <div className="border border-line-2 rounded-r-lg overflow-hidden bg-raise-2" data-id="element-594">
              <div className="flex items-center gap-3 px-5 py-3.5 border-b border-line" data-id="element-595">
                <span className="flex gap-2" aria-hidden="true" data-id="element-596">
                  <span className="w-3 h-3 rounded-full bg-[#ff5f57]" data-id="element-597" />
                  <span className="w-3 h-3 rounded-full bg-[#febc2e]" data-id="element-598" />
                  <span className="w-3 h-3 rounded-full bg-[#28c840]" data-id="element-599" />
                </span>
                <span className="ml-3 flex items-center gap-2 flex-1 max-w-md mono text-xs text-mut-2 bg-canvas border border-line rounded-full px-4 py-1.5" data-id="element-600">
                  <LockIcon size={12} data-id="element-601" />
                  www.novacloud-ke.com
                </span>
              </div>
              <img src={COVER} alt="NovaCloud brand — where innovation meets technology" className="w-full h-auto block" loading="lazy" data-id="element-602" />
            </div>
            <figcaption className="mono text-xs uppercase tracking-wider text-mut-2 mt-4" data-id="element-603">
              Brand & web — desktop
            </figcaption>
          </figure>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch" data-id="element-604">
            {/* Brand collateral */}
            <figure data-id="element-605">
              <div className="border border-line rounded-r-lg overflow-hidden bg-white h-full" data-id="element-606">
                <img src={COLLATERAL} alt="NovaCloud brand collateral — poster, caps and apparel" className="w-full h-full object-cover block" loading="lazy" data-id="element-607" />
              </div>
              <figcaption className="mono text-xs uppercase tracking-wider text-mut-2 mt-4 text-center" data-id="element-608">
                Brand collateral — merch & print
              </figcaption>
            </figure>

            {/* Logo + pattern lockup */}
            <figure data-id="element-609">
              <div className="border border-line rounded-r-lg overflow-hidden bg-white h-full flex flex-col" data-id="element-610">
                <div className="flex-1 flex items-center justify-center p-10 min-h-[180px]" data-id="element-611">
                  <img src={LOGO} alt="NovaCloud wordmark with the radiating sunburst mark" className="max-w-[70%] h-auto" loading="lazy" data-id="element-612" />
                </div>
                <div className="h-24 w-full border-t border-line" style={{
                backgroundImage: `url(${PATTERN})`,
                backgroundSize: '160px',
                backgroundRepeat: 'repeat'
              }} aria-hidden="true" data-id="element-613" />
              </div>
              <figcaption className="mono text-xs uppercase tracking-wider text-mut-2 mt-4 text-center" data-id="element-614">
                Wordmark & motif
              </figcaption>
            </figure>
          </div>
        </section>

        <section className="sec" data-id="element-615">
          <div className="sec__head mb-12" data-id="element-616">
            <h2 data-id="element-617">The Results</h2>
          </div>
          <div className="cards" data-id="element-618">
            <div className="card" data-id="element-619">
              <div className="text-4xl font-bold text-lime mb-4" data-id="element-620">1×</div>
              <h3 className="text-xl mb-2" data-id="element-621">One identity</h3>
              <p data-id="element-622">Across brand, merch & web</p>
            </div>
            <div className="card" data-id="element-623">
              <div className="text-4xl font-bold text-lime mb-4" data-id="element-624">5 wks</div>
              <h3 className="text-xl mb-2" data-id="element-625">Launch-ready</h3>
              <p data-id="element-626">From kickoff to delivery</p>
            </div>
            <div className="card" data-id="element-627">
              <div className="text-4xl font-bold text-lime mb-4" data-id="element-628">100%</div>
              <h3 className="text-xl mb-2" data-id="element-629">Custom built</h3>
              <p data-id="element-630">Designed & developed site</p>
            </div>
          </div>
        </section>

        <section className="sec border-t border-line mt-12 pt-24" data-id="element-631">
          <div className="text-center flex flex-col items-center" data-id="element-632">
            <div className="eyebrow mb-6" data-id="element-633">Next Project</div>
            <Link to="/work/flowkey" className="group inline-flex flex-col items-center" data-id="element-634">
              <h2 className="text-5xl md:text-7xl font-bold tracking-tight mb-10 text-ink group-hover:text-lime transition-colors duration-300" data-id="element-635">
                FlowKey <span className="serif" data-id="element-636">Labs</span>
              </h2>
            </Link>
            <div className="flex flex-wrap gap-4 justify-center" data-id="element-637">
              <CtaButton to="/#contact" variant="pill" data-id="element-638">
                Book Free Consultation
              </CtaButton>
              <CtaButton to="/#work" variant="ghost" data-id="element-639">
                Back to all work
              </CtaButton>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-640" />
    </>;
}